import {
  ClipboardCheck,
  FileText,
  ShieldCheck,
  Users,
  MessageSquareCode,
  Rocket,
  type LucideIcon,
} from 'lucide-react';

export interface MethodPillar {
  id: string;
  icon: LucideIcon;
  number: string;
  title: string;
  subtitle: string;
  forExecutives: string;
  forEngineers: string;
  details: { title: string; description: string }[];
  isOriginal?: boolean;
}

export const pillars: MethodPillar[] = [
  {
    id: 'plan-first',
    icon: ClipboardCheck,
    number: '01',
    title: 'Plan-First',
    subtitle: '計画駆動開発',
    forExecutives:
      'AIにまず計画を立てさせ、人間が承認してから実装に入ります。要件の誤解による作り直しが発生しないため、開発期間とコストの両方を大幅に削減できます。',
    forEngineers:
      'Claude CodeのPlanモードでコードベースを読み取り専用で分析し、実装計画を策定。人間がレビュー・承認した後にAuto-Acceptモードで一気に実行します。計画段階でトークン消費の40〜60%を削減でき、手戻りも最小化されます。',
    details: [
      {
        title: '読み取り専用の分析フェーズ',
        description:
          'AIがコードを変更する前に、既存のコードベース全体を読み取り専用で分析。影響範囲を正確に把握してから計画を立てます。',
      },
      {
        title: '人間によるレビューと承認',
        description:
          'AIが作成した計画を人間が確認・修正。認識のズレを実装前に解消します。',
      },
      {
        title: '承認後の高速実行',
        description:
          '承認済みの計画に基づき、AIが迷いなく一気に実装。計画なしの開発と比べて手戻りが大幅に減少します。',
      },
    ],
  },
  {
    id: 'structured-instructions',
    icon: FileText,
    number: '02',
    title: 'Structured Instructions',
    subtitle: '構造化されたAI指示',
    forExecutives:
      'プロジェクトごとに「AI専用の指示書」を整備します。これにより、どのタイミングで・誰が開発しても、同じ品質・同じルールでAIが動作します。属人化しない開発体制を実現します。',
    forEngineers:
      'CLAUDE.md（プロジェクトの憲法）を階層構造で設計。グローバル指示 → プロジェクト指示 → ディレクトリ別指示の3層で、AIの振る舞いをスコープに応じて制御します。Opus 4.6は長い指示書でも遵守率が高いため、必要十分な情報を網羅できます。',
    details: [
      {
        title: 'プロジェクトの憲法',
        description:
          'コーディング規約、テスト手順、アーキテクチャの方針をAIが常に参照する指示書として整備。毎回同じ品質を担保します。',
      },
      {
        title: '階層化による柔軟な制御',
        description:
          '全社共通ルール、プロジェクト固有ルール、機能別ルールを階層的に管理。必要な情報だけをAIに渡します。',
      },
      {
        title: 'ミスからの学習と蓄積',
        description:
          'AIがミスした場合、その原因と対策をルールとして追記。同じミスは二度と起きない仕組みを構築します。',
      },
    ],
  },
  {
    id: 'quality-automation',
    icon: ShieldCheck,
    number: '03',
    title: 'Quality by Automation',
    subtitle: '自動化による品質保証',
    forExecutives:
      '「AIが書いたコード＝品質が低い」は誤解です。テストの自動実行やコードフォーマットの強制など、人間以上に厳格な品質チェックを自動化しています。納品物の信頼性は従来の開発と同等以上です。',
    forEngineers:
      'Hooksシステムで「should-do（やるべき）」ではなく「must-do（必ずやる）」を実現。PreToolUseフックでコミット前のテスト実行を強制し、PostToolUseフックで自動フォーマットを適用。TDD（テスト駆動開発）ではテスト作成・実装・検証を分離し、AIの自己カンニングを防止します。',
    details: [
      {
        title: 'テスト駆動開発（TDD）',
        description:
          '実装前にテストを書き、テストが通ることだけを目的にコードを書く。AIが書いたコードだからこそ、自動テストによる品質保証が重要です。',
      },
      {
        title: 'Hooks による強制実行',
        description:
          'コミット前のテスト実行、編集後の自動フォーマットなど、品質に関わる処理をAIの判断に任せず、システムレベルで強制実行します。',
      },
      {
        title: 'テストと実装のコンテキスト分離',
        description:
          'テストを書くAIと実装するAIを分離。AIが自分のテストに合わせて「手を抜く」ことを構造的に防止します。',
      },
    ],
  },
  {
    id: 'multi-agent',
    icon: Users,
    number: '04',
    title: 'Multi-Agent Division',
    subtitle: 'マルチエージェント分業',
    forExecutives:
      '1人のエンジニアが複数のAIエージェントを同時に動かし、10人分の速度で開発します。フロントエンド・バックエンド・テストを並列で進めるため、従来数週間かかっていた作業が数日で完了します。',
    forEngineers:
      'Git Worktreeで独立した作業ブランチを複数作成し、各ブランチで別のClaude Codeインスタンスを起動。iTerm2の5プロファイル体制（プロンプトCC・計画CC・実装CC・検証CC・調査CC）で役割を分離し、コンテキスト汚染なく並列開発を実現します。',
    details: [
      {
        title: '独立したコンテキスト',
        description:
          '各エージェントが独立したコンテキストを持つため、情報の混線が起きません。フロントエンド担当はフロントに、バックエンド担当はバックエンドに集中できます。',
      },
      {
        title: '役割ベースの分業',
        description:
          'プロンプト作成、計画、実装、検証、調査の5つの役割を明確に分離。「考える」「設計する」「作る」「確認する」「調べる」が同時進行します。',
      },
      {
        title: '統合とマージ',
        description:
          '各エージェントの成果物はGitで管理。コンフリクトの少ない設計でブランチをマージし、品質を保ったまま統合します。',
      },
    ],
  },
  {
    id: 'ai-writes-prompts',
    icon: MessageSquareCode,
    number: '05',
    title: 'AI Writes Prompts',
    subtitle: 'AIがAIに指示を書く',
    isOriginal: true,
    forExecutives:
      '「やりたいこと」をざっくり伝えるだけで、AIが最適な設計図に変換してから開発に入ります。お客様の曖昧な要望でも、認識のズレによる手戻りが起きません。これがSenriganの開発スピードの秘密です。',
    forEngineers:
      '人間のプロンプトは往々にして曖昧・不完全です。プロンプトCC（指示書作成専門）が要件を分析し、実装CCに渡す精密な指示書を自動生成。人間がレビューしてOKなら実装CCにコピペ。プロンプトエンジニアリングをAI自身に委任することで、人間のプロンプト品質に依存しない安定した開発が可能になります。',
    details: [
      {
        title: '曖昧さの排除',
        description:
          '人間の「こんな感じで」をAIが「具体的にはこの仕様で」に変換。要件の解釈違いによる手戻りをゼロに近づけます。',
      },
      {
        title: 'プロンプトの品質安定化',
        description:
          '人間のスキルや体調に左右されず、常に高品質なプロンプトを生成。開発品質のムラをなくします。',
      },
      {
        title: 'レビュー可能な指示書',
        description:
          'AIが生成した指示書を人間がレビューできるため、「AIが何をしようとしているか」を事前に完全に把握できます。ブラックボックス化しません。',
      },
    ],
  },
  {
    id: 'real-proof',
    icon: Rocket,
    number: '06',
    title: 'Real Project Proof',
    subtitle: '実案件での証明',
    forExecutives:
      '理論ではなく、実際のクライアント案件でこのメソッドが機能しています。初回打ち合わせからわずか2日で詳細な計画書が完成。従来のシステム会社なら数週間かかる工程を、AI開発で大幅に短縮しています。',
    forEngineers:
      '2026年3月のRODOプロジェクト（ロードサービス業務統合システム）では、2時間のヒアリング議事録をClaude Codeに入力し、全9章・Phase 0〜4の段階的開発計画書をHTML/PDFで自動生成。技術構成・機能一覧・アーキテクチャ設計まで含む本格的な提案書を翌日に納品しました。',
    details: [
      {
        title: 'Day 1: ヒアリングと議事録',
        description:
          '社長・現場責任者・事務担当者と2時間の会議。業務フロー、課題、要望を詳細に記録。',
      },
      {
        title: 'Day 2: AI による計画書生成',
        description:
          '議事録をClaude Codeに入力。全9章構成の計画書（背景・課題・システム概要・機能一覧・技術構成・開発フェーズ）をHTML/PDFで自動生成。',
      },
      {
        title: '従来比: 数週間 → 2日',
        description:
          '従来のシステム会社であれば提案書作成に2〜3週間。同品質のドキュメントをAI活用で2日に短縮。コストも大幅削減。',
      },
    ],
  },
];
